import { navigate } from "gatsby";
import _ from "lodash";
import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import MediaQuery from "react-responsive";
import Scroll, { Link } from "react-scroll";
import styled, { css } from "styled-components";
import Hamburger from "hamburger-react";

import DropDownMenu from "src/components/menu/DropDownMenu";
import images from "src/images";
import { translate, translateLocale, convertLocale } from "src/locales";
import { colors } from "src/styles/colors";
import zIndex from "src/styles/zIndexes";
import { media, desktopWidth, mobileWidth } from "src/utils/media";
import { GNB, Badge } from "src/components/Typography";
import { gTagTracking } from "src/decorators/analyticsTracking";
import { CategoryType, ActionType } from "src/configs/analytics";
import { env } from "src/configs/env";
import { getDashboardUrl } from "src/utils/uri";
import Menus from "src/components/menu/Menus";
import { isBrowser } from "src/utils/navigator";
import ISMSBanner from "src/components/banner/ISMSBanner";
import { getLinksByLanguage } from "src/constants/link";

type Props = { className?: string; isKo: boolean } & WithTranslation;

type States = {
  isMenuOpen: boolean;
  hash: string | null;
};

const Events = Scroll.Events;

const Container = styled.div<{ $isKo: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: #fff;
  transform: translate3d(0, 0, 0);
  z-index: ${zIndex.hundred};
  ${({ $isKo }) =>
    $isKo
      ? css`
          height: calc(74px + 52px);
          ${media.mobile`
    height: calc(54px + 52px);
  `}
        `
      : css`
          height: 74px;
          ${media.mobile`
    height: 54px;
  `}
        `}
`;

const ISMSBannerView = styled(ISMSBanner)<{ $isKo: boolean }>`
  ${({ $isKo }) =>
    $isKo
      ? css`
          display: flex;
        `
      : css`
          display: none;
        `}
`;

const Inner = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 1440px;
  width: 100%;
  padding: 0px 44px;
  height: 74px;
  ${media.mobile`
    width: 100%;
    height: 54px;
    padding-left: 20px;
    padding-right: 4px;
  `}
`;

const DesktopLeftView = styled.div`
  position: absolute;
  top: 0px;
  display: flex;
  flex: 1;
  height: 74px;
  flex-direction: row;
  align-items: center;
`;

const MobileView = styled.div`
  display: flex;
  flex: 1;
  height: 56px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  ${media.desktop` 
    display: none;
  `}
`;

const LogoLink = styled(Link)``;

const Logo = styled.img`
  width: 87px;
  height: 18px;
  cursor: pointer;
  margin-top: 3px;
`;

const Title = styled(GNB)`
  color: ${colors.text.dark.title};
`;

const DesktopHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  ${media.mobile`
    display: none;
  `}
`;

const DesktopMenuItems = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding-left: 66px;
`;

const DesktopDropDownMenus = styled(Menus)`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  padding-left: 12px;
  padding-right: 12px;
  margin-right: 8px;
  ${media.mobile` 
    display: none;
    `}
`;

const MenuItem = styled(Link)`
  width: 100%;
`;

const MenuItemText = styled.div<{ $active: boolean }>``;

const RightView = styled.div`
  position: absolute;
  top: 0px;
  right: 32px;
  display: flex;
  height: 74px;
  flex-direction: row;
  align-items: center;
`;

const DeveloperButton = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const DashboardButton = styled(GNB)`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: #4c5359;
  margin-right: 28px;
  cursor: pointer;
  background: #fafbfc;
  border-radius: 100px;
  padding: 4px 6px 4px 12px;
`;

const LinkIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-left: 8px;
`;

const ArrowRightIcon = styled.img`
  width: 20px;
`;

const LocaleIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 6px;
`;

const LocaleName = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-family: Archivo;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 16.8px;
  color: ${colors.text.dark.body};
  text-align: center;
  transition: all 0.15s linear 0s;
  cursor: pointer;
`;

const DropDownMenuView = styled(DropDownMenu)<{ $isMenuOpen: boolean }>`
  ${({ $isMenuOpen }) =>
    $isMenuOpen
      ? css`
          display: flex;
        `
      : css`
          display: none;
        `}
  ${media.desktop`
    display: none;
  `}
`;

class Header extends React.Component<Props, States> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isMenuOpen: false,
      hash: null,
    };
  }

  public componentDidMount() {
    if (isBrowser) {
      window.addEventListener("scroll", this.onScroll);
      Events.scrollEvent.register("end", this.onScrollEnd);
      return;
    }
  }

  public componentWillUnmount() {
    if (isBrowser) {
      window.removeEventListener("scroll", this.onScroll);
      Events.scrollEvent.remove("end");
      return;
    }
  }

  public render() {
    const { className, isKo, t } = this.props;
    const { isMenuOpen, hash } = this.state;
    const { language } = this.props.i18n;
    const byHash: Record<string, string> = {
      "#multisigWallet": translate(["header", "menus", "multisigWallet"], t),
      "#security": translate(["header", "menus", "security"], t),
      "#saveGasFee": translate(["header", "menus", "saveGasFee"], t),
      "#dashboardAndAPI": translate(["header", "menus", "dashboardAndAPI"], t),
      "#currencies": translate(["header", "menus", "currencies"], t),
    };

    return (
      <>
        <Container className={className} $isKo={isKo}>
          <ISMSBannerView $isKo={isKo} />
          <Inner>
            <DesktopHeader key="desktop-logo">
              <DesktopLeftView>
                <LogoLink to="main" smooth={true} spy={true} hashSpy={true}>
                  <Logo
                    alt="HENESIS"
                    src={images.henesisLogoSrc}
                    srcSet={images.henesisLogo}
                    onClick={this.onHomeLink}
                  />
                </LogoLink>
              </DesktopLeftView>
              <RightView>
                <DashboardButton onClick={this.navigateToDashboard}>
                  {translate(["header", "dashboard"], t)}
                  <ArrowRightIcon
                    src={images.arrowRightGreySrc}
                    srcSet={images.arrowRightGrey}
                  />
                </DashboardButton>
                <LocaleName onClick={this.changeLocales}>
                  <LocaleIcon src={images.localeSrc} srcSet={images.locale} />
                  {translateLocale.get(convertLocale(language))}
                </LocaleName>
              </RightView>
              <DesktopMenuItems>
                <DesktopDropDownMenus
                  active={true}
                  menuText={
                    <MenuItem
                      key={"desktopProduct"}
                      to="multisigWallet"
                      smooth={true}
                      spy={true}
                      hashSpy={true}
                      isDynamic={true}
                      onClick={_.partial(this.onProductLink, "multisigWallet")}
                    >
                      {translate(["header", "product"], t)}
                    </MenuItem>
                  }
                  menus={[
                    <MenuItem
                      key={"desktopMultisigWallet"}
                      activeClass="active-menu-item"
                      to="multisigWallet"
                      smooth={true}
                      spy={true}
                      hashSpy={true}
                      isDynamic={true}
                      onClick={_.partial(this.onProductLink, "multisigWallet")}
                    >
                      <MenuItemText $active={false}>
                        {translate(["header", "menus", "multisigWallet"], t)}
                      </MenuItemText>
                    </MenuItem>,
                    <MenuItem
                      key={"desktopSecurity"}
                      activeClass="active-menu-item"
                      to="security"
                      smooth={true}
                      spy={true}
                      hashSpy={true}
                      isDynamic={true}
                      onClick={_.partial(this.onProductLink, "security")}
                    >
                      <MenuItemText $active={false}>
                        {translate(["header", "menus", "security"], t)}
                      </MenuItemText>
                    </MenuItem>,
                    <MenuItem
                      key={"desktopSaveGasFee"}
                      activeClass="active-menu-item"
                      to="saveGasFee"
                      smooth={true}
                      spy={true}
                      hashSpy={true}
                      isDynamic={true}
                      onClick={_.partial(this.onProductLink, "saveGasFee")}
                    >
                      <MenuItemText $active={false}>
                        {translate(["header", "menus", "saveGasFee"], t)}
                      </MenuItemText>
                    </MenuItem>,
                    <MenuItem
                      key={"desktopDashboardAndAPI"}
                      activeClass="active-menu-item"
                      to="dashboardAndAPI"
                      smooth={true}
                      spy={true}
                      hashSpy={true}
                      isDynamic={true}
                      onClick={_.partial(this.onProductLink, "dashboardAndAPI")}
                    >
                      <MenuItemText $active={false}>
                        {translate(["header", "menus", "dashboardAndAPI"], t)}
                      </MenuItemText>
                    </MenuItem>,
                    <MenuItem
                      key={"desktopCurrencies"}
                      activeClass="active-menu-item"
                      to="currencies"
                      smooth={true}
                      spy={true}
                      hashSpy={true}
                      isDynamic={true}
                      onClick={_.partial(this.onProductLink, "currencies")}
                    >
                      <MenuItemText $active={false}>
                        {translate(["header", "menus", "currencies"], t)}
                      </MenuItemText>
                    </MenuItem>,
                  ]}
                />
                <DesktopDropDownMenus
                  active={true}
                  menuText={translate(["header", "developers"], t)}
                  menus={[
                    <DeveloperButton
                      key={"desktopDocs"}
                      onClick={_.partial(
                        this.navigateToLink,
                        getLinksByLanguage(language).docs
                      )}
                    >
                      {"Docs"}
                      <LinkIcon
                        src={images.externalicLinkSrc}
                        srcSet={images.externalicLink}
                      />
                    </DeveloperButton>,
                    <DeveloperButton
                      key={"desktopTutorials"}
                      onClick={_.partial(
                        this.navigateToLink,
                        getLinksByLanguage(language).tutorials
                      )}
                    >
                      {"Tutorials"}
                      <LinkIcon
                        src={images.externalicLinkSrc}
                        srcSet={images.externalicLink}
                      />
                    </DeveloperButton>,
                    <DeveloperButton
                      key={"desktopApis"}
                      onClick={_.partial(
                        this.navigateToLink,
                        getLinksByLanguage(language).apis
                      )}
                    >
                      {"APIs"}
                      <LinkIcon
                        src={images.externalicLinkSrc}
                        srcSet={images.externalicLink}
                      />
                    </DeveloperButton>,
                  ]}
                />
              </DesktopMenuItems>
            </DesktopHeader>
            <MobileView key="mobile-logo">
              {!isMenuOpen && byHash[hash ?? ""] ? (
                <Title>{byHash[hash ?? ""] ?? ""}</Title>
              ) : (
                <LogoLink to="main" smooth={true} spy={true} hashSpy={true}>
                  <Logo
                    alt="HENESIS"
                    src={images.henesisLogoSrc}
                    srcSet={images.henesisLogo}
                    onClick={this.onHomeLink}
                  />
                </LogoLink>
              )}
              <Hamburger
                size={18}
                toggled={isMenuOpen}
                toggle={this.toggleMenuOpen}
                onToggle={this.onToggleMenuOpen}
              />
            </MobileView>
          </Inner>
        </Container>
        <DropDownMenuView
          $isMenuOpen={isMenuOpen}
          isKo={isKo}
          onHomeLink={this.onHomeLink}
          onProductLink={this.onProductLink}
          onLocaleChange={this.changeLocales}
        />
      </>
    );
  }

  private onToggleMenuOpen = (isMenuOpen: boolean) => {
    this.setState({
      isMenuOpen,
    });
  };

  // private get renderMenuItems() {
  //   const { t } = this.props;
  //   const { hash } = this.state;
  //   const emptyHash = _.isEmpty(hash);
  //   return (

  //   );
  // }

  private toggleMenuOpen = () => {
    this.setState((prevState) => {
      return {
        isMenuOpen: !prevState.isMenuOpen,
      };
    });
  };

  @gTagTracking((__, ___, args) => {
    const action: ActionType = args[0];
    return {
      category: CategoryType.GNB,
      action,
    };
  })
  private onMenuClick = (action: ActionType) => {
    // NOTHING
  };

  private onProductLink = (hash: string) => {
    const { i18n } = this.props;
    const pathname = window.location.pathname;

    if (pathname.includes("/product")) {
      return;
    }
    navigate(`/${i18n.language}/product#${hash}`);
  };

  @gTagTracking((__, ___, args) => {
    return {
      category: CategoryType.GNB,
      action: ActionType.HenesisWallet,
    };
  })
  private navigateToDashboard = () => {
    window.open(getDashboardUrl());
  };

  private navigateToLink = (uri: string) => {
    window.open(uri);
  };

  @gTagTracking<Props, any>((props, __, ___) => {
    const { i18n } = props;
    const label = i18n.language === "ko" ? "ENG" : "KOR";
    return {
      category: CategoryType.GNB,
      action: ActionType.Language,
      label,
    };
  })
  private changeLocales = () => {
    const { i18n } = this.props;
    const pathname = window.location.pathname;
    const changeLanguage = i18n.language === "ko" ? "en" : "ko";
    const newPathname = pathname.replace(
      `/${i18n.language}`,
      `/${changeLanguage}`
    );
    navigate(newPathname);
  };

  private onHomeLink = () => {
    const { i18n } = this.props;
    navigate(`/${i18n.language}`);
  };

  private onScrollEnd = (to: string) => {
    if (!to) {
      return;
    }
    this.setState({
      isMenuOpen: false,
      hash: `#${to}`,
    });
  };

  private onScroll = () => {
    this.setState({
      hash: window.location.hash,
    });
  };
}

export default withTranslation()(Header);
